import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Stack,
  Button,
  Grid,
  Chip,
  TextField,
  Rating,
  CircularProgress,
  IconButton,
  Divider,
  FormHelperText,
  Alert,
  AlertTitle,
  alpha,
  useTheme
} from '@mui/material';
import {
  Star as StarIcon,
  Check as CheckIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  InfoOutlined as InfoIcon,
  LockOutlined as LockIcon,
  PhotoCamera as PhotoCameraIcon,
  Delete as DeleteIcon,
  ErrorOutline as ErrorIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import { useAuth } from '../../context/AuthContext';

/**
 * ReviewForm - Formulaire multi-step de création d'évaluation
 * 
 * Features:
 * - 4 étapes: Overall → Equipment → Process → Experience
 * - Upload photos avec preview
 * - Validation en temps réel
 * - Sélection tags positifs/négatifs
 * - i18n complète FR/EN/AR
 * - Gestion erreurs avec useErrorHandler
 * - Vérification éligibilité (réservation terminée)
 * 
 * @param {Object} props
 * @param {string} props.reservationId - ID de la réservation
 * @param {string} props.listingId - ID du listing
 * @param {Function} props.onSuccess - Callback après succès
 * @param {Function} props.onCancel - Callback annulation
 */
const MAX_PHOTOS = 10;
const MAX_PHOTO_SIZE = 5 * 1024 * 1024; // 5MB

const STEP_DEFINITION = [
  { key: 'overall', icon: StarIcon },
  { key: 'equipment', icon: StarIcon },
  { key: 'process', icon: StarIcon },
  { key: 'experience', icon: CheckIcon }
];

const EQUIPMENT_SECTIONS = [
  {
    ratingKey: 'equipment_quality_rating',
    commentKey: 'equipment_quality_comment',
    labelKey: 'criteria.equipment_quality',
    placeholderKey: 'form.equipment.quality_placeholder'
  },
  {
    ratingKey: 'equipment_cleanliness_rating',
    commentKey: 'equipment_cleanliness_comment',
    labelKey: 'criteria.equipment_cleanliness',
    placeholderKey: 'form.equipment.cleanliness_placeholder'
  },
  {
    ratingKey: 'equipment_state_rating',
    commentKey: 'equipment_state_comment',
    labelKey: 'criteria.equipment_state',
    placeholderKey: 'form.equipment.state_placeholder'
  }
];

const PROCESS_SECTIONS = [
  {
    ratingKey: 'process_pickup_rating',
    commentKey: 'process_pickup_comment',
    labelKey: 'criteria.process_pickup',
    placeholderKey: 'form.process.pickup_placeholder'
  },
  {
    ratingKey: 'process_return_rating',
    commentKey: 'process_return_comment',
    labelKey: 'criteria.process_return',
    placeholderKey: 'form.process.return_placeholder'
  },
  {
    ratingKey: 'process_communication_rating',
    commentKey: 'process_communication_comment',
    labelKey: 'criteria.process_communication',
    placeholderKey: 'form.process.communication_placeholder'
  }
];

const POSITIVE_TAGS = [
  'excellent_communication',
  'clean_equipment',
  'easy_pickup',
  'great_value',
  'flexible_owner',
  'responsive',
  'trustworthy',
  'well_maintained',
  'as_described',
  'convenient_location',
  'smooth_process',
  'professional'
];

const NEGATIVE_TAGS = [
  'poor_communication',
  'dirty_equipment',
  'difficult_pickup',
  'overpriced',
  'unresponsive',
  'not_as_described',
  'bad_condition',
  'hidden_fees',
  'late_response',
  'unfriendly',
  'complicated_process',
  'unreliable'
];

const ReviewForm = ({ reservationId, listingId, existingReview = null, editMode = false, isRejected = false, onSuccess, onCancel }) => {
  const { t: rawTReviews, ready, i18n } = useTranslation('reviews');
  const navigate = useNavigate();
  const { handleError, showSuccess } = useErrorHandler();
  const { user, isAuthenticated } = useAuth();
  const theme = useTheme();
  const formContainerRef = React.useRef(null);
  const stepperRef = React.useRef(null);

  const tr = useCallback((key, options = {}) => {
    if (!key || typeof key !== 'string') {
      return '';
    }

    const normalizedKey = key.startsWith('reviews.') ? key.replace('reviews.', '') : key;

    const translated = rawTReviews(normalizedKey, options);
    if (translated && translated !== normalizedKey) {
      return translated;
    }

    const resource = i18n.getResource(i18n.language, 'reviews', normalizedKey);
    if (resource) {
      return resource;
    }

    return key;
  }, [rawTReviews, i18n, i18n.language]);

  // État du formulaire multi-step
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [canReview, setCanReview] = useState(false);
  const [checkingEligibility, setCheckingEligibility] = useState(true);
  const [eligibilityMessage, setEligibilityMessage] = useState('');

  // Vérifier l'éligibilité pour laisser un avis
  useEffect(() => {
    // En mode édition, bypass la vérification d'éligibilité
    if (editMode) {
      setCanReview(true);
      setEligibilityMessage('');
      setCheckingEligibility(false);
      return;
    }

    // Attendre que les traductions soient prêtes
    if (!ready) return;

    // Forcer le chargement du namespace reviews
    i18n.loadNamespaces('reviews');

    const checkEligibility = async () => {
      // Si pas de reservationId ou listingId, on ne peut pas laisser d'avis
      if (!reservationId && !listingId) {
        setCanReview(false);
        setEligibilityMessage(tr('form.errors.no_reservation'));
        setCheckingEligibility(false);
        return;
      }

      // Si pas authentifié
      if (!isAuthenticated || !user) {
        setCanReview(false);
        setEligibilityMessage(tr('form.errors.login_required'));
        setCheckingEligibility(false);
        return;
      }

      // Si on a un reservationId, vérifier qu'elle est terminée
      if (reservationId) {
        try {
          const response = await api.get(`/reviews/can-review/${reservationId}`);

          const apiData = response.data.data.data;

          if (response.data.success && apiData.can_review) {
            setCanReview(true);
            setEligibilityMessage('');
          } else {
            setCanReview(false);
            const apiMessage = apiData.message;
            setEligibilityMessage(apiMessage ? tr(apiMessage) : tr('form.errors.cannot_review'));
          }
        } catch (error) {
          setCanReview(false);
          setEligibilityMessage(tr('form.errors.check_failed'));
        }
      } else {
        // Si on n'a qu'un listingId sans reservationId, on ne peut pas laisser d'avis
        setCanReview(false);
        setEligibilityMessage(tr('form.errors.reservation_required'));
      }
      
      setCheckingEligibility(false);
    };

    checkEligibility();
  }, [reservationId, listingId, isAuthenticated, user, tr, ready, editMode]);

  // Données du formulaire
  const [formData, setFormData] = useState(() => {
    // Si mode édition et données existantes, pré-remplir
    if (editMode && existingReview) {
      return {
        // Step 1: Overall
        overall_rating: existingReview.overall_rating || 0,
        overall_comment: existingReview.overall_comment || '',
        
        // Step 2: Equipment
        equipment_quality_rating: existingReview.equipment_quality_rating || 0,
        equipment_quality_comment: existingReview.equipment_quality_comment || '',
        equipment_cleanliness_rating: existingReview.equipment_cleanliness_rating || 0,
        equipment_cleanliness_comment: existingReview.equipment_cleanliness_comment || '',
        equipment_state_rating: existingReview.equipment_state_rating || 0,
        equipment_state_comment: existingReview.equipment_state_comment || '',
        
        // Step 3: Process
        process_pickup_rating: existingReview.process_pickup_rating || 0,
        process_pickup_comment: existingReview.process_pickup_comment || '',
        process_return_rating: existingReview.process_return_rating || 0,
        process_return_comment: existingReview.process_return_comment || '',
        process_communication_rating: existingReview.process_communication_rating || 0,
        process_communication_comment: existingReview.process_communication_comment || '',
        
        // Step 4: Experience
        experience_value_rating: existingReview.experience_value_rating || 0,
        experience_value_comment: existingReview.experience_value_comment || '',
        experience_recommend: existingReview.experience_recommend !== null ? existingReview.experience_recommend : 
                             (existingReview.would_recommend !== undefined ? existingReview.would_recommend : null),
        positive_tags: existingReview.positive_tags || [],
        negative_tags: existingReview.negative_tags || [],
        photos: []
      };
    }
    
    console.log('✨ Mode création - formulaire vide');
    // Sinon, valeurs par défaut
    return {
      // Step 1: Overall
      overall_rating: 0,
      overall_comment: '',
      
      // Step 2: Equipment
      equipment_quality_rating: 0,
      equipment_quality_comment: '',
      equipment_cleanliness_rating: 0,
      equipment_cleanliness_comment: '',
      equipment_state_rating: 0,
      equipment_state_comment: '',
      
      // Step 3: Process
      process_pickup_rating: 0,
      process_pickup_comment: '',
      process_return_rating: 0,
      process_return_comment: '',
      process_communication_rating: 0,
      process_communication_comment: '',
      
      // Step 4: Experience
      experience_value_rating: 0,
      experience_value_comment: '',
      experience_recommend: null,
      positive_tags: [],
      negative_tags: [],
      photos: []
    };
  });

  // État validation
  const [errors, setErrors] = useState({});
  const [photoFiles, setPhotoFiles] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState(() => {
    // Pré-charger les photos existantes si mode édition
    if (editMode && existingReview?.equipment_photos) {
      return existingReview.equipment_photos;
    }
    return [];
  });

  // Mettre à jour formData quand existingReview change (chargement async)
  useEffect(() => {
    if (editMode && existingReview) {      
      setFormData({
        // Step 1: Overall
        overall_rating: existingReview.overall_rating || 0,
        overall_comment: existingReview.overall_comment || '',
        
        // Step 2: Equipment
        equipment_quality_rating: existingReview.equipment_quality_rating || 0,
        equipment_quality_comment: existingReview.equipment_quality_comment || '',
        equipment_cleanliness_rating: existingReview.equipment_cleanliness_rating || 0,
        equipment_cleanliness_comment: existingReview.equipment_cleanliness_comment || '',
        equipment_state_rating: existingReview.equipment_state_rating || 0,
        equipment_state_comment: existingReview.equipment_state_comment || '',
        
        // Step 3: Process
        process_pickup_rating: existingReview.process_pickup_rating || 0,
        process_pickup_comment: existingReview.process_pickup_comment || '',
        process_return_rating: existingReview.process_return_rating || 0,
        process_return_comment: existingReview.process_return_comment || '',
        process_communication_rating: existingReview.process_communication_rating || 0,
        process_communication_comment: existingReview.process_communication_comment || '',
        
        // Step 4: Experience
        experience_value_rating: existingReview.experience_value_rating || 0,
        experience_value_comment: existingReview.experience_value_comment || '',
        experience_recommend: existingReview.experience_recommend !== null ? existingReview.experience_recommend : 
                             (existingReview.would_recommend !== undefined ? existingReview.would_recommend : null),
        positive_tags: existingReview.positive_tags || [],
        negative_tags: existingReview.negative_tags || [],
        photos: []
      });
      
      // Mettre à jour les photos aussi
      if (existingReview.equipment_photos) {
        setPhotoPreviews(existingReview.equipment_photos);
      }
    }
  }, [editMode, existingReview]);

  // Gestion changement valeur
  const handleChange = useCallback((name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error pour ce champ
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors]);

  // Gestion tags
  const toggleTag = useCallback((tagType, tag) => {
    setFormData(prev => ({
      ...prev,
      [tagType]: prev[tagType].includes(tag)
        ? prev[tagType].filter(t => t !== tag)
        : [...prev[tagType], tag]
    }));
  }, []);

  // Gestion upload photos
  const handlePhotoUpload = useCallback((e) => {
    const files = Array.from(e.target.files);
    if (photoFiles.length + files.length > MAX_PHOTOS) {
      toast.error(tr('form.errors.max_photos', { max: MAX_PHOTOS }));
      return;
    }

    // Validation taille
    const invalidFiles = files.filter(f => f.size > MAX_PHOTO_SIZE);
    if (invalidFiles.length > 0) {
      toast.error(tr('form.errors.photo_too_large', { max: '5MB' }));
      return;
    }

    // Validation type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const invalidTypes = files.filter(f => !validTypes.includes(f.type));
    if (invalidTypes.length > 0) {
      toast.error(tr('form.errors.invalid_photo_format'));
      return;
    }

    // Créer previews
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreviews(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });

    setPhotoFiles(prev => [...prev, ...files]);
  }, [photoFiles.length, tr]);

  // Supprimer photo
  const removePhoto = useCallback((index) => {
    setPhotoFiles(prev => prev.filter((_, i) => i !== index));
    setPhotoPreviews(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Validation step
  const validateStep = useCallback((step) => {
    const newErrors = {};

    switch (step) {
      case 1:
        // Overall rating requis
        if (!formData.overall_rating) {
          newErrors.overall_rating = tr('form.errors.rating_required');
        }
        // Commentaire requis et minimum 20 caractères
        if (!formData.overall_comment || formData.overall_comment.trim().length < 20) {
          newErrors.overall_comment = tr('form.errors.comment_too_short', { min: 20 });
        }
        break;

      case 2:
        // TOUS les ratings equipment sont requis
        if (!formData.equipment_quality_rating || formData.equipment_quality_rating === 0) {
          newErrors.equipment_quality_rating = tr('form.errors.rating_required');
        }
        if (!formData.equipment_cleanliness_rating || formData.equipment_cleanliness_rating === 0) {
          newErrors.equipment_cleanliness_rating = tr('form.errors.rating_required');
        }
        if (!formData.equipment_state_rating || formData.equipment_state_rating === 0) {
          newErrors.equipment_state_rating = tr('form.errors.rating_required');
        }
        break;

      case 3:
        // TOUS les ratings process sont requis
        if (!formData.process_pickup_rating || formData.process_pickup_rating === 0) {
          newErrors.process_pickup_rating = tr('form.errors.rating_required');
        }
        if (!formData.process_return_rating || formData.process_return_rating === 0) {
          newErrors.process_return_rating = tr('form.errors.rating_required');
        }
        if (!formData.process_communication_rating || formData.process_communication_rating === 0) {
          newErrors.process_communication_rating = tr('form.errors.rating_required');
        }
        break;

      case 4:
        // Experience value requis
        if (!formData.experience_value_rating) {
          newErrors.experience_value_rating = tr('form.errors.rating_required');
        }
        // Recommend requis
        if (formData.experience_recommend === null) {
          newErrors.experience_recommend = tr('form.errors.recommend_required');
        }
        break;
    }

    setErrors(newErrors);
    
    // Si des erreurs existent, scroller vers la première erreur
    if (Object.keys(newErrors).length > 0) {
      setTimeout(() => {
        const firstErrorKey = Object.keys(newErrors)[0];
        const errorElement = document.querySelector(`[name="${firstErrorKey}"]`) || 
                           document.querySelector(`[data-error-key="${firstErrorKey}"]`) ||
                           document.querySelector('.MuiFormHelperText-root.Mui-error');
        
        if (errorElement) {
          errorElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        }
      }, 100);
    }
    
    return Object.keys(newErrors).length === 0;
  }, [formData, tr]);

  // Navigation steps
  const nextStep = useCallback(() => {
    console.log(`🔄 nextStep - Étape actuelle: ${currentStep}`);
    if (validateStep(currentStep)) {
      console.log(`✅ Validation OK - Navigation vers étape ${currentStep + 1}`);
      setCurrentStep(prev => Math.min(prev + 1, 4));
      // Scroller vers le stepper (haut du formulaire)
      setTimeout(() => {
        const targetRef = stepperRef.current || formContainerRef.current;
        if (targetRef) {
          targetRef.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start',
            inline: 'nearest'
          });
        }
      }, 100);
    } else {
      console.log(`❌ Validation échouée - reste à l'étape ${currentStep}`);
    }
  }, [currentStep, validateStep]);

  const prevStep = useCallback(() => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    // Scroller vers le stepper (haut du formulaire)
    setTimeout(() => {
      const targetRef = stepperRef.current || formContainerRef.current;
      if (targetRef) {
        targetRef.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest'
        });
      }
    }, 100);
  }, []);

  // Soumission formulaire
  const handleSubmit = async (e) => {
    console.log('🚀 handleSubmit - Type:', e?.type, '| Target:', e?.target?.tagName, '| Step:', currentStep);
    
    // Empêcher la soumission par défaut si c'est un événement
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // SÉCURITÉ: Empêcher la soumission si on n'est pas à l'étape 4
    if (currentStep !== 4) {
      console.warn('⚠️ Tentative de soumission bloquée - étape:', currentStep);
      return;
    }

    if (!validateStep(4)) {
      return;
    }

    console.log('✅ Soumission autorisée - démarrage...');
    setIsSubmitting(true);

    try {
      const computeAverage = (values) => {
        const valid = values.filter((value) => typeof value === 'number' && value > 0);
        if (!valid.length) {
          return null;
        }
        const sum = valid.reduce((total, value) => total + value, 0);
        return Math.round((sum / valid.length) * 10) / 10;
      };

      const buildCriteria = (entries) => {
        return entries.reduce((acc, [key, value]) => {
          if (typeof value === 'number' && value > 0) {
            acc[key] = value;
          }
          return acc;
        }, {});
      };

      const equipmentRatings = [
        formData.equipment_quality_rating,
        formData.equipment_cleanliness_rating,
        formData.equipment_state_rating
      ];
      const processRatings = [
        formData.process_pickup_rating,
        formData.process_return_rating,
        formData.process_communication_rating
      ];
      const experienceRatings = [formData.experience_value_rating];

      const equipmentComment = [
        formData.equipment_quality_comment,
        formData.equipment_cleanliness_comment,
        formData.equipment_state_comment
      ].filter(Boolean).join('\n');

      const processComment = [
        formData.process_pickup_comment,
        formData.process_return_comment,
        formData.process_communication_comment
      ].filter(Boolean).join('\n');

      const preparedData = {
        ...formData,
        reservation_id: reservationId,
        listing_id: listingId,
        review_source: 'web',
        language_code: i18n.language,
        equipment_rating: computeAverage(equipmentRatings),
        equipment_comment: equipmentComment || undefined,
        equipment_criteria: buildCriteria([
          ['functionality', formData.equipment_quality_rating],
          ['cleanliness', formData.equipment_cleanliness_rating],
          ['condition', formData.equipment_state_rating]
        ]),
        equipment_photos: photoPreviews && photoPreviews.length > 0 ? photoPreviews : [],
        process_rating: computeAverage(processRatings),
        process_comment: processComment || undefined,
        process_criteria: buildCriteria([
          ['pickup_ease', formData.process_pickup_rating],
          ['return_ease', formData.process_return_rating],
          ['documentation_clarity', formData.process_communication_rating]
        ]),
        experience_rating: computeAverage(experienceRatings),
        experience_comment: formData.experience_value_comment || undefined,
        experience_criteria: buildCriteria([
          ['overall_satisfaction', formData.experience_value_rating]
        ]),
        would_recommend: formData.experience_recommend === true,
        would_rent_again: formData.experience_recommend === true,
        positive_tags: formData.positive_tags || [],
        negative_tags: formData.negative_tags || []
      };

      delete preparedData.photos;

      // Nettoyer les ratings individuels à 0 (le backend n'accepte pas 0, seulement null ou >= 1)
      const ratingFields = [
        'equipment_quality_rating',
        'equipment_cleanliness_rating',
        'equipment_state_rating',
        'process_pickup_rating',
        'process_return_rating',
        'process_communication_rating',
        'experience_value_rating'
      ];

      ratingFields.forEach(field => {
        if (preparedData[field] === 0) {
          delete preparedData[field];
        }
      });

      // Mode édition: PATCH, sinon POST
      const response = editMode && existingReview
        ? await api.patch(`/reviews/reservations/${existingReview.id}`, preparedData)
        : await api.post('/reviews/reservations', preparedData);

      console.log('📝 Review response:', response.data);

      if (editMode) {
        showSuccess('success.review_updated');
      }
      
      if (onSuccess) {
        onSuccess(response.data.data);
      } else {
        navigate(`/listings/${listingId}`);
      }

    } catch (error) {
      handleError(error, 'review_creation');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render Step Content
  const stepperItems = useMemo(() => STEP_DEFINITION, []);

  const renderRatingBlock = (section) => {
    const { ratingKey, commentKey, labelKey, placeholderKey } = section;
    return (
      <Card 
        key={ratingKey} 
        variant="outlined" 
        sx={{ 
          borderRadius: 3,
          borderColor: errors[ratingKey] ? 'error.main' : 'divider',
          borderWidth: errors[ratingKey] ? 2 : 1,
          bgcolor: errors[ratingKey] ? 'error.lighter' : 'background.paper'
        }}
      >
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Stack spacing={3}>
            <Box data-error-key={ratingKey}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {tr(labelKey)}
                {errors[ratingKey] && (
                  <Typography component="span" color="error" sx={{ ml: 1, fontSize: '0.875rem' }}>
                    *
                  </Typography>
                )}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                <Rating
                  name={ratingKey}
                  value={formData[ratingKey] || 0}
                  onChange={(_, value) => handleChange(ratingKey, value || 0)}
                  precision={1}
                  size="large"
                  sx={{
                    '& .MuiRating-icon': errors[ratingKey] ? {
                      color: 'error.main'
                    } : {}
                  }}
                />
                {formData[ratingKey] > 0 && (
                  <Chip
                    color="primary"
                    variant="outlined"
                    label={`${formData[ratingKey]}/5`}
                    icon={<StarIcon fontSize="small" />}
                  />
                )}
              </Stack>
              {errors[ratingKey] && (
                <FormHelperText error sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <ErrorIcon fontSize="small" />
                  {errors[ratingKey]}
                </FormHelperText>
              )}
            </Box>
            <TextField
              multiline
              minRows={3}
              maxRows={6}
              value={formData[commentKey]}
              onChange={(e) => handleChange(commentKey, e.target.value)}
              placeholder={tr(placeholderKey)}
              inputProps={{ maxLength: 500 }}
              helperText={`${formData[commentKey]?.length || 0}/500`}
            />
          </Stack>
        </CardContent>
      </Card>
    );
  };

  const renderOverallStep = () => (
    <Card variant="outlined" sx={{ borderRadius: 3 }}>
      <CardHeader
        title={
          <Typography variant="h6" component="span" fontWeight={700}>
            {tr('form.overall.title')}
          </Typography>
        }
        subheader={tr('form.overall.description')}
        sx={{ '& .MuiCardHeader-subheader': { color: 'text.secondary', lineHeight: 1.6 } }}
      />
      <CardContent sx={{ p: { xs: 3, md: 4 } }}>
        <Stack spacing={3}>
          <Box data-error-key="overall_rating">
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              {`${tr('form.overall.rating_label')} *`}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
              <Rating
                name="overall_rating"
                value={formData.overall_rating || 0}
                onChange={(_, value) => handleChange('overall_rating', value || 0)}
                size="large"
              />
              {formData.overall_rating > 0 && (
                <Chip
                  color="primary"
                  variant="outlined"
                  icon={<StarIcon fontSize="small" />}
                  label={`${formData.overall_rating}/5`}
                />
              )}
            </Stack>
            {errors.overall_rating && (
              <FormHelperText error sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <ErrorIcon fontSize="small" />
                {errors.overall_rating}
              </FormHelperText>
            )}
          </Box>

          <TextField
            name="overall_comment"
            
            required
            multiline
            minRows={4}
            maxRows={8}
            value={formData.overall_comment}
            onChange={(e) => handleChange('overall_comment', e.target.value)}
            placeholder={tr('form.overall.comment_placeholder')}
            inputProps={{ maxLength: 1000 }}
            error={Boolean(errors.overall_comment)}
            helperText={
              errors.overall_comment
                ? errors.overall_comment
                : `${tr('form.minimum_chars', { min: 20 })} • ${formData.overall_comment.length}/1000`
            }
          />
        </Stack>
      </CardContent>
    </Card>
  );

  const renderEquipmentStep = () => (
    <Stack spacing={3}>
      <Card variant="outlined" sx={{ borderRadius: 3 }}>
        <CardHeader
          title={<Typography variant="h6" fontWeight={700}>{tr('form.equipment.title')}</Typography>}
          subheader={tr('form.equipment.description')}
          sx={{ '& .MuiCardHeader-subheader': { color: 'text.secondary', lineHeight: 1.6 } }}
        />
      </Card>
      {EQUIPMENT_SECTIONS.map(renderRatingBlock)}
    </Stack>
  );

  const renderProcessStep = () => (
    <Stack spacing={3}>
      <Card variant="outlined" sx={{ borderRadius: 3 }}>
        <CardHeader
          title={<Typography variant="h6" fontWeight={700}>{tr('form.process.title')}</Typography>}
          subheader={tr('form.process.description')}
          sx={{ '& .MuiCardHeader-subheader': { color: 'text.secondary', lineHeight: 1.6 } }}
        />
      </Card>
      {PROCESS_SECTIONS.map((section) =>
        renderRatingBlock({ ...section, errorKey: section.ratingKey })
      )}
    </Stack>
  );

  const renderRecommendGroup = () => (
    <Box data-error-key="experience_recommend">
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        {`${tr('form.experience.recommend_label')} *`}
      </Typography>
      <Stack direction="row" spacing={2}>
        <Box
          onClick={() => handleChange('experience_recommend', true)}
          sx={{
            flex: 1,
            p: 2.5,
            borderRadius: 2,
            border: `2px solid ${formData.experience_recommend === true ? '#4caf50' : theme.palette.divider}`,
            backgroundColor: formData.experience_recommend === true ? alpha('#4caf50', 0.1) : 'transparent',
            transition: 'all 0.2s ease',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1
          }}
        >
          <CheckIcon sx={{ color: formData.experience_recommend === true ? '#4caf50' : theme.palette.text.secondary }} />
          <Typography variant="body2" fontWeight={formData.experience_recommend === true ? 600 : 500}>
            {tr('form.experience.recommend_yes')}
          </Typography>
        </Box>
        <Box
          onClick={() => handleChange('experience_recommend', false)}
          sx={{
            flex: 1,
            p: 2.5,
            borderRadius: 2,
            border: `2px solid ${formData.experience_recommend === false ? '#f44336' : theme.palette.divider}`,
            backgroundColor: formData.experience_recommend === false ? alpha('#f44336', 0.1) : 'transparent',
            transition: 'all 0.2s ease',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1
          }}
        >
          <CloseIcon sx={{ color: formData.experience_recommend === false ? '#f44336' : theme.palette.text.secondary }} />
          <Typography variant="body2" fontWeight={formData.experience_recommend === false ? 600 : 500}>
            {tr('form.experience.recommend_no')}
          </Typography>
        </Box>
      </Stack>
      {errors.experience_recommend && (
        <FormHelperText error sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <ErrorIcon fontSize="small" />
          {errors.experience_recommend}
        </FormHelperText>
      )}
    </Box>
  );

  const renderTags = (tags, selected, type, color) => (
    <Stack direction="row" flexWrap="wrap" spacing={1} useFlexGap>
      {tags.map((tag) => {
        const isSelected = selected.includes(tag);
        return (
          <Chip
            key={tag}
            label={tr(`tags.${tag}`)}
            color={isSelected ? color : 'default'}
            variant={isSelected ? 'filled' : 'outlined'}
            onClick={() => toggleTag(type, tag)}
            icon={isSelected ? <CheckIcon fontSize="small" /> : undefined}
            sx={{ borderRadius: 999, px: 1.5, py: 0.5 }}
          />
        );
      })}
    </Stack>
  );

  const renderPhotosSection = () => (
    <Card variant="outlined" sx={{ borderRadius: 3 }}>
      <CardContent sx={{ p: { xs: 3, md: 4 } }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              {tr('form.experience.photos_label')}
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                ({tr('form.experience.photos_max', { max: MAX_PHOTOS })})
              </Typography>
            </Typography>
            <Button
              variant="contained"
              startIcon={<PhotoCameraIcon />}
              component="label"
              disabled={photoFiles.length >= MAX_PHOTOS}
            >
              {tr('form.experience.upload_photos')}
              <input
                hidden
                type="file"
                multiple
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handlePhotoUpload}
              />
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              {tr('form.experience.photo_requirements')}
            </Typography>
          </Box>

          {photoPreviews.length > 0 && (
            <Grid container spacing={2}>
              {photoPreviews.map((preview, index) => (
                <Grid key={`${index}-${preview}`} item xs={6} sm={4} md={3}>
                  <Box
                    sx={{
                      position: 'relative',
                      borderRadius: 2,
                      overflow: 'hidden',
                      boxShadow: 2,
                      height: 140
                    }}
                  >
                    <Box
                      component="img"
                      src={preview}
                      alt={`${tr('form.experience.photo_alt', 'Photo')} ${index + 1}`}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => removePhoto(index)}
                      sx={{
                        position: 'absolute',
                        top: 6,
                        right: 6,
                        backgroundColor: 'rgba(0,0,0,0.6)',
                        color: '#fff',
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </Stack>
      </CardContent>
    </Card>
  );

  const renderExperienceStep = () => (
    <Stack spacing={3}>
      <Card variant="outlined" sx={{ borderRadius: 3 }}>
        <CardHeader
          title={<Typography variant="h6" fontWeight={700}>{tr('form.experience.title')}</Typography>}
          subheader={tr('form.experience.description')}
          sx={{ '& .MuiCardHeader-subheader': { color: 'text.secondary', lineHeight: 1.6 } }}
        />
      </Card>

      <Card variant="outlined" sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {`${tr('criteria.experience_value')} *`}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                <Rating
                  name="experience_value_rating"
                  value={formData.experience_value_rating || 0}
                  onChange={(_, value) => handleChange('experience_value_rating', value || 0)}
                  size="large"
                />
                {formData.experience_value_rating > 0 && (
                  <Chip
                    color="success"
                    variant="outlined"
                    icon={<StarIcon fontSize="small" />}
                    label={`${formData.experience_value_rating}/5`}
                  />
                )}
              </Stack>
              {errors.experience_value_rating && (
                <FormHelperText error sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <ErrorIcon fontSize="small" />
                  {errors.experience_value_rating}
                </FormHelperText>
              )}
            </Box>

            <TextField
              multiline
              minRows={3}
              maxRows={6}
              value={formData.experience_value_comment}
              onChange={(e) => handleChange('experience_value_comment', e.target.value)}
              placeholder={tr('form.experience.value_placeholder')}
              inputProps={{ maxLength: 500 }}
              helperText={`${formData.experience_value_comment?.length || 0}/500`}
            />

            {renderRecommendGroup()}

            <Divider />

            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {tr('form.experience.positive_tags_label')}
              </Typography>
              {renderTags(POSITIVE_TAGS, formData.positive_tags, 'positive_tags', 'success')}
            </Box>

            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {tr('form.experience.negative_tags_label')}
              </Typography>
              {renderTags(NEGATIVE_TAGS, formData.negative_tags, 'negative_tags', 'error')}
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {renderPhotosSection()}
    </Stack>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderOverallStep();
      case 2:
        return renderEquipmentStep();
      case 3:
        return renderProcessStep();
      case 4:
        return renderExperienceStep();
      default:
        return null;
    }
  };

  // Afficher un loader si les traductions ne sont pas encore prêtes
  if (!ready) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8, gap: 2 }}>
        <CircularProgress />
        <Typography color="text.secondary">Chargement...</Typography>
      </Box>
    );
  }

  // Afficher message de chargement pendant la vérification d'éligibilité
  if (checkingEligibility) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8, gap: 2 }}>
        <CircularProgress />
        <Typography color="text.secondary">{tr('form.checking_eligibility')}</Typography>
      </Box>
    );
  }

  // Afficher message si l'utilisateur ne peut pas laisser d'avis
  if (!canReview) {
    return (
      <Card variant="outlined" sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Stack direction="row" spacing={2.5} alignItems="flex-start">
            <Box sx={{ color: 'warning.main' }}>
              {isAuthenticated ? <InfoIcon /> : <LockIcon />}
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                {!isAuthenticated ? tr('form.errors.login_required_title') : tr('form.errors.cannot_review_title')}
              </Typography>
              <Typography color="text.secondary" sx={{ mb: isAuthenticated ? 0 : 2 }}>
                {eligibilityMessage}
              </Typography>
              {!isAuthenticated && (
                <Button variant="contained" color="warning" onClick={() => navigate('/login')}>
                  {tr('form.errors.login_button')}
                </Button>
              )}
            </Box>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box ref={formContainerRef}>
      <Box ref={stepperRef} sx={{ mb: 5 }}>
        <Stepper activeStep={currentStep - 1} alternativeLabel>
          {stepperItems.map((step, index) => {
            const IconComponent = step.icon;
            const StepIconComponent = (props) => {
              const { active, completed } = props;
              return (
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: completed ? 'success.contrastText' : active ? 'primary.contrastText' : 'text.secondary',
                    backgroundColor: completed ? 'success.main' : active ? 'primary.main' : 'background.paper',
                    border: `2px solid ${completed || active ? 'transparent' : 'divider'}`,
                    boxShadow: completed || active ? 2 : 0
                  }}
                >
                  {completed ? <CheckIcon /> : <IconComponent />}
                </Box>
              );
            };

            return (
              <Step key={step.key} completed={currentStep - 1 > index}>
                <StepLabel StepIconComponent={StepIconComponent}>
                  <Typography variant="caption" fontWeight={600}>
                    {tr(`form.steps.${step.key}`)}
                  </Typography>
                </StepLabel>
              </Step>
            );
          })}
        </Stepper>
      </Box>

      {/* Alerte d'erreur globale */}
      {Object.keys(errors).length > 0 && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          icon={<ErrorIcon />}
        >
          <AlertTitle>{tr('form.errors.validation_failed')}</AlertTitle>
          {tr('form.errors.please_fill_required_ratings')}
        </Alert>
      )}

      <Box 
        component="form" 
        onSubmit={handleSubmit}
        noValidate
        autoComplete="off"
        onKeyDown={(e) => {
          // Empêcher la soumission du formulaire avec Enter (sauf sur le bouton submit)
          if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
        onClick={(e) => {
          // Empêcher les clics involontaires qui pourraient déclencher submit
          if (e.target.type === 'submit' && currentStep < 4) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
        sx={{
        backgroundColor: 'background.paper',
        borderRadius: 4,
        boxShadow: 4,
        p: { xs: 3, md: 4 },
        border: '1px solid',
        borderColor: 'divider'
      }}>
        {renderStepContent()}

        <Divider sx={{ my: 4 }} />

        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2}>
          {currentStep > 1 ? (
            <Button
              type="button"
              variant="text"
              startIcon={<ArrowBackIcon />}
              onClick={prevStep}
              sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
            >
              {tr('form.buttons.previous')}
            </Button>
          ) : (
            <Box />
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <Button
              type="button"
              variant="outlined"
              color="inherit"
              onClick={onCancel}
              disabled={isSubmitting}
              sx={{ minWidth: 140 }}
            >
              {tr('form.buttons.cancel')}
            </Button>

            {currentStep < 4 ? (
              <Button
                type="button"
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                onClick={nextStep}
                sx={{ minWidth: 160 }}
              >
                {tr('form.buttons.next')}
              </Button>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', width: '100%', maxWidth: 200 }}>
                {isSubmitting && (
                  <Box
                    sx={{
                      width: '100%',
                      height: 4,
                      borderRadius: 2,
                      background: 'linear-gradient(90deg, #ffb74d, #ff9800, #f44336, #ffeb3b, #ff9800)',
                      backgroundSize: '200% 100%',
                      animation: 'flameTrail 1.2s linear infinite',
                      mb: 1,
                      '@keyframes flameTrail': {
                        '0%': {
                          backgroundPosition: '0% 0%'
                        },
                        '100%': {
                          backgroundPosition: '-200% 0%'
                        }
                      }
                    }}
                  />
                )}
                <Button
                  type="button"
                  variant="contained"
                  color="success"
                  disabled={isSubmitting || isRejected}
                  onClick={(e) => {
                    console.log('🖱️ Clic sur bouton Submit');
                    handleSubmit(e);
                  }}
                  sx={{ minWidth: 200 }}
                  endIcon={!isSubmitting ? <CheckIcon /> : undefined}
                >
                  {isSubmitting ? (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <CircularProgress color="inherit" size={18} thickness={5} />
                      <span>{tr('form.buttons.submitting')}</span>
                    </Stack>
                  ) : isRejected ? (
                    tr('form.buttons.rejectedCannotSubmit')
                  ) : (
                    tr('form.buttons.submit')
                  )}
                </Button>
              </Box>
            )}
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default ReviewForm;
